import React from 'react';
import { AlertErrorCode, CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { Set } from '../../ts/models/Set';
import { IError } from '../../httpClient/serverApi/types/IError';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { IErrorDetail, MultipleItemResultNotificationValues } from './ActionMessages';
import { SimpleActionToastMessages } from './ActionHelpers';
import { Formatters, Messages } from '../../codegen/Localize';
import { ActionFailureDetailsDialogContent } from './ActionFailureDetailsDialog';
import { ReactModalService } from '../dialogs/ReactModalService';
import { DialogType, DialogWidget } from '@tableau/widgets';
import { TabDialogWidth } from '../dialogs/style/TabDialogWidth';

export namespace ContentActionNotification {

  /**
   * Displays a single toast that describes the result of an action, either success or failure.
   */
  export function displaySingleToastForResult(params: SimpleActionToastMessages & {
    items: Set<INamedItemInfo>;
    errors: ReadonlyArray<IError>;
  }): void {

    const { items, errors, successMsg, errorMsg } = params;

    const errorCount = errors ? errors.length : 0;
    const successCount = items.size() - errorCount;

    const notificationLines: string[] = [];

    if (successCount) {
      notificationLines.push(successMsg({successCount}));
    }

    if (errorCount) {
      if (errors !== undefined && errors[0].id === undefined) {
        notificationLines.push(buildGenericErrorMessage(errors[0]));
      } else {
        notificationLines.push(errorMsg({ errorCount }));
      }
    }

    ToasterService.instance.pop({ type: errorCount ? ToastType.Error : ToastType.Info, text: notificationLines.join('\n') });
  }

  /**
   * Displays a success and/or a failure toast that describe the result of an action on multiple items.
   *
   * If the action succeeded on some items but failed on others, both toasts will appear.
   *
   * The failure toast includes a "details" link that launches a dialog showing an itemized list of
   * each item on which the action failed, and its associated error.
   *
   * **Note:** For the majority of use cases, consider using `ActionHelpers.displayStandardToastsForResult`,
   * which wraps this function in some additional error handling and a more convenient API.
   */
  export function displayItemizedToastsForResult(options: MultipleItemResultNotificationValues) {

    if (options.successSummary) {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: options.successSummary,
        actionMessage: options.successActionMessage,
        action: options.successAction
      });
    }

    if (options.errorSummary) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: options.errorSummary,
        actionMessage: Messages.details(),
        action: () => showItemizedErrorDetailsDialog(options.errorDetails),
      });
    }
  }

  export function displayToastForGenericError(error: IError) {
    return displayFailureToast(buildGenericErrorMessage(error));
  }

  export function displaySuccessToast(successMessage: string) {
    ToasterService.instance.pop({ type: ToastType.Info, text: successMessage });
  }

  export function displayFailureToast(failureMessage: string) {
    ToasterService.instance.pop({ type: ToastType.Error, text: failureMessage });
  }
}

/**
 * Displays the dialog that shows a data grid with the error details.
 */
function showItemizedErrorDetailsDialog(errorDetails: IErrorDetail[]) {
  return ReactModalService.openCustom(closeAndDismiss => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      title: Messages.errorsTooltipTitle(),
      type: DialogType.NormalWithTitleBar,
      dialogWidth: TabDialogWidth.width700px,
      showCloseButton: true,
    }, closeAndDismiss);

    return <DialogWidget {...dialogProps}>
      <ActionFailureDetailsDialogContent
        errorDetails={errorDetails}
        closeDialog={closeAndDismiss.close}
      />
    </DialogWidget>;
  });
}

/**
 * Builds message for a generic error. This is used when the ErrorItem from the API response
 * doesn't have an associated item ID.
 */
function buildGenericErrorMessage(error: IError): string {
  switch(error.code) {
    case CommonErrorCode.TooManyRequests:
      return buildThrottlingMessage(error);
    case AlertErrorCode.AlertConnectionlessFailure:
      return Messages.credentialMissedOrWrong();
  }
  return Messages.errorUnknown();
}

/**
 * Constructs throttling error message "Too many requests. Please retry after {text}".
 * Text can be in hours, hours and minutes, or seconds, depending on the numeric value.
 */
export function buildThrottlingMessage(error: IError) {
  let retryAfter: number = error.errorMetaData.retryAfter;
  let text: string = '';

  if (retryAfter >= 3600) {
    const hours = Math.floor(retryAfter/3600);
    text = text + Messages.hours({ HOURS: hours, HOURS_LOC: Formatters.formatCount(hours) });
    retryAfter %= 3600;
  }
  if (retryAfter >= 60) {
    const minutes = Math.floor(retryAfter/60);
    const minutesText: string = Messages.minutes({ MINUTES: minutes, MINUTES_LOC: Formatters.formatCount(minutes) });
    text = text ? `${text} ${minutesText}` : minutesText;
    retryAfter = 0;
  }
  // if hours or minutes is set, don't include seconds
  if (retryAfter > 0 && text === '') {
    const seconds = Math.floor(retryAfter);
    text = Messages.seconds({ SECONDS: seconds, SECONDS_LOC: Formatters.formatCount(seconds) });
  }
  return Messages.tooManyRequests({ RETRY_AFTER_TEXT: text });
}
